import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Group, IconSwipe, Illustration, Tabs, Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { MultiListContainer } from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useSelectedCollections } from 'features/multicollection-edit/contexts/SelectedCollectionsContext';
import { useDragScroll } from 'hooks/useDragScroll';
import styles from './MultiList.module.scss';
import { getNextTabValue } from './MultiList.utils';

interface MultiListProps extends CommonEntityProps {
  data: MultiListContainer;
}

export const MultiList = ({ data: multiList, parentHierarchyId, index }: MultiListProps) => {
  const { generateHierarchyId, renderEntity } = useDataModelContext();
  const { selectedCheckboxes, allUnchecked, activeTabId, setActiveTabId, scrollContainerRef, scrollPositionsRef } =
    useSelectedCollections();
  const hierarchyId = generateHierarchyId(multiList, parentHierarchyId, index);
  const tabsRef = useRef<HTMLElement>(null);
  const [showRightArrow, setShowRightArrow] = useState(false);
  useDragScroll(tabsRef);

  useLayoutEffect(() => {
    const next = getNextTabValue(activeTabId, multiList.collection, selectedCheckboxes);
    if (next !== activeTabId) setActiveTabId(next);
  }, [activeTabId, multiList.collection, selectedCheckboxes, setActiveTabId]);

  // Save the outgoing tab's scrollTop before it unmounts, then update activeTabId.
  const handleTabChange = useCallback(
    (nextTabId: string) => {
      const container = scrollContainerRef.current;
      if (container && activeTabId && activeTabId !== nextTabId) {
        scrollPositionsRef.current[activeTabId] = container.scrollTop;
      }
      setActiveTabId(nextTabId);
    },
    [activeTabId, scrollContainerRef, scrollPositionsRef, setActiveTabId]
  );

  // Restore the incoming tab's scroll position after the new content mounts.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = scrollPositionsRef.current[activeTabId] ?? 0;
  }, [activeTabId, scrollContainerRef, scrollPositionsRef]);

  const updateRightArrow = useCallback(() => {
    const scrollEl = tabsRef.current;
    if (!scrollEl) return;
    setShowRightArrow(scrollEl.scrollLeft + scrollEl.clientWidth < scrollEl.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const scrollEl = tabsRef.current;
    if (!scrollEl) return undefined;
    updateRightArrow();
    scrollEl.addEventListener('scroll', updateRightArrow);
    const ro = new ResizeObserver(updateRightArrow);
    ro.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener('scroll', updateRightArrow);
      ro.disconnect();
    };
  }, [updateRightArrow]);

  // Re-check when tabs are added or removed, since scrollWidth changes
  useEffect(() => {
    updateRightArrow();
  }, [selectedCheckboxes, updateRightArrow]);

  return (
    <>
      {allUnchecked && (
        <Group direction="column" align="center" valign="center" fullWidth className={styles.emptyState} gap="none">
          <Illustration name="empty" className={styles.illustration} margin={{ top: 'xl' }} />
          <Typography variant="header4" color="asphalt" align="center" margin={{ top: 'mlg', bottom: 'md' }}>
            Your Workspace is Empty
          </Typography>
          <Typography variant="bodytext" color="asphalt" align="center">
            Select a collection from the sidebar to begin.
            <br /> Checking a collection creates an active tab, simply drag
            <br /> stories from the search results to populate your feed.
          </Typography>
        </Group>
      )}
      <Wrapper
        ref={scrollContainerRef}
        data-hierarchy-id={hierarchyId}
        data-model-uuid={multiList.uuid}
        padding={{ horizontal: 'mlg' }}
        className={classnames({ [styles.hidden]: allUnchecked, [styles.multilist]: !allUnchecked })}
      >
        <Wrapper className={styles.tabsWrapper}>
          <Tabs
            ref={tabsRef}
            role="tablist"
            onChange={handleTabChange}
            value={activeTabId}
            margin={{ bottom: 'md' }}
            className={styles.tabs}
          >
            {multiList.collection
              // Render only tabs for collections selected in the sidebar
              .filter((collection) => selectedCheckboxes[collection.uuid])
              .map((collection) => (
                <Tabs.Item
                  key={collection.uuid}
                  role="tab"
                  id={`tab-${collection.uuid}`}
                  value={collection.uuid}
                  aria-selected={activeTabId === collection.uuid ? 'true' : 'false'}
                  aria-controls={`tabpanel-${collection.uuid}`}
                >
                  {collection.attributes.name}
                </Tabs.Item>
              ))}
          </Tabs>
          {showRightArrow && <IconSwipe color="asphalt" size="md" className={styles.tabsRightArrow} />}
        </Wrapper>
        {multiList.collection
          .filter((collection) => selectedCheckboxes[collection.uuid])
          .map((collection) => {
            const collIndex = multiList.collection.indexOf(collection);
            return (
              <Wrapper
                key={collection.uuid}
                role="tabpanel"
                id={`tabpanel-${collection.uuid}`}
                aria-labelledby={`tab-${collection.uuid}`}
                style={activeTabId !== collection.uuid ? { display: 'none' } : undefined}
              >
                {renderEntity(collection, {
                  hierarchyId,
                  index: collIndex
                })}
              </Wrapper>
            );
          })}
      </Wrapper>
    </>
  );
};
